import type { ErrorResponse } from '@forbiz/contracts'

/**
 * API 클라이언트. web과 admin이 공유한다.
 *
 * 세션은 httpOnly 쿠키로 오가므로 credentials: 'include'가 필수다.
 * 응답 타입은 호출부가 계약(@forbiz/contracts)에서 가져와 지정한다.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields?: Record<string, string>,
  ) {
    super(message)
  }
}

/**
 * 401 전역 핸들러. 세션이 중간에 만료되면 어떤 요청이든 401을 받는데,
 * 그때 AuthProvider가 user를 비워 로그인 화면으로 보내도록 등록한다.
 * 등록 전(초기 마운트 전)의 401은 무시된다.
 */
let unauthorizedHandler: (() => void) | null = null
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  unauthorizedHandler = fn
}

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    // 세션 만료 등 인증 상실은 전역 핸들러로 알린다(로그인 화면으로 유도).
    if (res.status === 401) unauthorizedHandler?.()
    let payload: ErrorResponse | null = null
    try {
      payload = (await res.json()) as ErrorResponse
    } catch {
      // JSON이 아니면 그대로 둔다
    }
    if (payload?.error) {
      throw new ApiError(res.status, payload.error.code, payload.error.message, payload.error.fields)
    }
    throw new ApiError(res.status, 'UNKNOWN', `요청이 실패했습니다 (${res.status})`)
  }
  return (await res.json()) as T
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  // exactOptionalPropertyTypes를 켰으므로 body: undefined를 넘기지 않는다.
  const init: RequestInit = { method, credentials: 'include' }
  if (body !== undefined) {
    init.headers = { 'content-type': 'application/json' }
    init.body = JSON.stringify(body)
  }
  return handle<T>(await fetch(path, init))
}

/**
 * 파일 업로드. multipart/form-data라 JSON 헬퍼로 못 보낸다. content-type은 넣지
 * 않는다(브라우저가 boundary 포함해 자동 설정). 에러·401 처리는 request와 동일하게 —
 * 업로드 중 세션이 만료돼도 전역 핸들러로 로그인 화면에 유도된다.
 */
async function upload<T>(path: string, form: FormData): Promise<T> {
  return handle<T>(await fetch(path, { method: 'POST', credentials: 'include', body: form }))
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
  upload: <T>(path: string, form: FormData) => upload<T>(path, form),
}
